// Service to call Process Optimization endpoints
import api from "./api";
import { normalizeOptimizeJson } from "../utils/optimizeNormalize";
// Base URL comes from REACT_APP_PROCESS_OPT_BASE_URL or falls back to main API URL
const BASE_URL =
  process.env.REACT_APP_PROCESS_OPT_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  "";

function buildHeaders(extra = {}) {
  let token = null;
  try {
    const raw = sessionStorage.getItem("auth");
    if (raw) token = JSON.parse(raw)?.accessToken || null;
  } catch (_) { }
  return {
    Accept: "application/json,image/png,*/*",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === "AbortError") throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
      throw err;
    });
}

async function postJson(path, timeoutMs = 30_000, body = undefined) {
  const url = `${BASE_URL}${path}`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: buildHeaders(),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    },
    timeoutMs,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function getJson(path, timeoutMs = 30_000) {
  const url = `${BASE_URL}${path}`;
  const res = await fetchWithTimeout(url, { method: "GET", headers: buildHeaders() }, timeoutMs);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

// Poll /cms/jobs/{job_id} every 4 s until status === "done" or "error".
// Max 75 attempts (~5 minutes) before giving up.
async function pollJob(jobId, intervalMs = 4000) {
  const MAX_POLLS = 75;
  for (let i = 0; i < MAX_POLLS; i++) {
    const data = await getJson(`/cms/jobs/${encodeURIComponent(jobId)}`);
    if (data.status === "done")  return data.result;
    if (data.status === "error") throw new Error(data.error || "Job failed on server");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Job timed out after 5 minutes. Please try again.");
}

export async function getOptimizationData(processId, goalWeights, options = {}) {
  if (!processId) throw new Error("processId is required");

  // Two endpoints for one pipeline, because only one of them takes the flag:
  // `include_subprocess_redesigns` exists on POST /cms/optimize/{id}/json only —
  // /start accepts the query param but silently drops it (verified: the job result
  // comes back with no subprocess_redesign_projection at all). So the flag forces
  // the synchronous route, and the default path keeps using the job queue, which is
  // what makes large processes survive a proxy timeout.
  if (options.includeSubprocessRedesigns) {
    // One extra full greedy run per sub-process, all inside this one request —
    // hence the much longer ceiling than the 30 s the job-start call needs.
    const raw = await postJson(
      `/cms/optimize/${encodeURIComponent(processId)}/json?include_subprocess_redesigns=true`,
      300_000,
      goalWeights || undefined,
    );
    return normalizeOptimizeJson(raw || {});
  }

  // Start the async job (responds in < 1 s). `goalWeights` = Devil's-Quadrangle
  // weights {time, cost, quality, flexibility} for scenario ranking (optional).
  const { job_id } = await postJson(
    `/cms/optimize/${encodeURIComponent(processId)}/start`,
    30_000,
    goalWeights || undefined,
  );
  // Poll until the result is ready
  const raw = await pollJob(job_id);
  return normalizeOptimizeJson(raw || {});
}

/** LLM propose-and-confirm: suggested probabilities for gateway branches that have
 *  none (analyst reviews & saves — nothing is written by this call; branches the
 *  analyst already set are never touched). */
export async function suggestBranchProbabilities(processId) {
  if (!processId) throw new Error("processId is required");
  return postJson(
    `/cms/flow/probability-suggestions/${encodeURIComponent(processId)}`,
    45_000,
  );
}

// Fetch optimized process version (workflow/gateways snapshot) for CMS consumption.
//
// Called with no `options` the endpoint behaves exactly as it always has: the agent's
// own redesign, no engine composition. Passing `engines` composes the agent's result
// with the RL and/or BPR runs the analyst just approved, and adds one new top-level
// key to the response — `redesign_provenance` (see readRedesignProvenance below).
// Everything else about the response shape is unchanged.
//
// `rlJobId` / `bprResultRef` are what make the composition reproduce the run the
// analyst actually saw. Without them the endpoint re-runs the engine, and RL being a
// stochastic search, that can persist a different redesign than the approved one.
//
// `includeSubprocessRedesigns` MUST match the flag the engine tab ran with: if a
// reference has expired and the engine re-runs, a mismatched flag produces a
// different redesign for the same job_id.
//
// `adopt` is the analyst's heuristic selection — the `selection_key` of every row
// they left ticked in the save checklist. It is authoritative for structure: an
// engine heuristic whose key is absent is NOT written. That makes the three states
// genuinely different and they are encoded differently on purpose:
//
//   adopt: undefined/null → param omitted → "first load, apply the engines' defaults"
//   adopt: []             → `adopt=`      → "I ticked nothing: change no structure"
//   adopt: [k1, k2]       → `adopt=k1,k2` → exactly this selection
//
// Collapsing the middle case into the first would silently re-apply everything the
// analyst had just cleared, so the empty array must survive all the way to the wire.
// The param requires `engines` (400 otherwise), hence the guard.
export async function getOptimizedVersion(processId, options = {}) {
  if (!processId) throw new Error("processId is required");
  const { engines, rlJobId, bprResultRef, includeSubprocessRedesigns, adopt } = options;
  const list = Array.isArray(engines) ? engines.filter(Boolean) : [];

  const params = new URLSearchParams();
  if (list.length) {
    // `engines` and the pin-path params (exclude_task_ids / include_parallel) are
    // mutually exclusive — sending both returns 400 CONFLICTING_STRUCTURE_SPEC.
    // Nothing here builds those, and this signature deliberately offers no way to,
    // so the Redesign Scenarios pin path stays the only caller that uses them.
    params.set("engines", list.join(","));
    if (rlJobId) params.set("rl_job_id", String(rlJobId));
    if (bprResultRef) params.set("bpr_result_ref", String(bprResultRef));
    params.set("include_subprocess_redesigns", includeSubprocessRedesigns ? "true" : "false");
    // URLSearchParams percent-encodes the `+` inside a pair key (rl:parallelism:994+995)
    // rather than letting it decode as a space on the far side — which is why the list
    // is joined here and never hand-built into the query string.
    if (Array.isArray(adopt)) params.set("adopt", adopt.filter(Boolean).join(","));
  }
  const qs = params.toString();
  const data = await getJson(
    `/save-optimized-version/${encodeURIComponent(processId)}${qs ? `?${qs}` : ""}`,
  );
  return data || {};
}

export async function getProcessVersionHistory(processCode) {
  const code = String(processCode || "").trim();
  if (!code) throw new Error("processCode is required");
  return api.get(
    `/process/process-version-history?process_code=${encodeURIComponent(code)}`,
  );
}

export async function getValueAnalysis(processId) {
  if (!processId) throw new Error("processId is required");
  // Start the async job (responds in < 1 s)
  const { job_id } = await postJson(
    `/cms/value-analysis/${encodeURIComponent(processId)}/start`,
  );
  // Poll until the result is ready
  return pollJob(job_id);
}
